// @ts-nocheck
import React, { useEffect, useState } from 'react';
import api from '../services/api';
import notify from '../utils/toast';
import { useAuth } from '../context/AuthContext';
import { useI18n } from '../context/I18nContext';
import { useTheme } from '../context/ThemeContext';

const COLOR_HEX = {
    green: '#1a5f2a', blue: '#1a4a7a', purple: '#5b2c6f', teal: '#0e6655', orange: '#b85c00',
};

export default function SystemAdmin() {
    const { user, hasRole } = useAuth();
    const { lang, setLang, t, tl } = useI18n();
    const { mode, toggleMode, color, setColor, colors } = useTheme();
    const [tab, setTab] = useState('modules');
    const [modules, setModules] = useState([]);
    const [users, setUsers] = useState([]);
    const [entity, setEntity] = useState('projects');
    const [deleted, setDeleted] = useState([]);
    const [entities, setEntities] = useState([]);
    const [loading, setLoading] = useState(true);
    const [company, setCompany] = useState(null);
    const [savingCompany, setSavingCompany] = useState(false);

    const loadModules = () => api.get('/system/modules').then((r) => setModules(r.data.data));
    const loadUsers = () => api.get('/system/users', { params: { with_deleted: '1' } }).then((r) => setUsers(r.data.data));
    const loadRecycle = () => {
        api.get('/system/recycle', { params: { entity } }).then((r) => {
            const d = r.data.data;
            // FIXED: backend now always returns { items, entities }
            setDeleted(Array.isArray(d) ? d : (d?.items || []));
            setEntities(d?.entities || []);
        }).catch(() => setDeleted([]));
    };
    const loadCompany = () => api.get('/system/company').then((r) => setCompany(r.data.data)).catch(() => {});

    useEffect(() => {
        setLoading(true);
        Promise.all([loadModules(), loadUsers(), loadCompany()]).finally(() => setLoading(false));
    }, []);

    useEffect(() => { if (tab === 'recycle') loadRecycle(); }, [tab, entity]);

    const toggleModule = async (id, is_active) => {
        if (!hasRole('super_admin')) return notify.info('Super admin only');
        await api.post(`/system/modules/${id}/toggle`, { is_active: !is_active });
        loadModules();
    };
    const setActive = async (id, is_active) => { await api.post(`/system/users/${id}/active`, { is_active }); loadUsers(); };
    const softDelUser = async (id) => { if (!window.confirm('Soft-delete this user?')) return; await api.delete(`/system/users/${id}`); loadUsers(); };
    const restoreUser = async (id) => { await api.post(`/system/users/${id}/restore`); loadUsers(); };
    const restoreEntity = async (id) => { await api.post(`/system/recycle/${entity}/${id}/restore`); loadRecycle(); };

    const saveCompany = async (e) => {
        e.preventDefault();
        setSavingCompany(true);
        try {
            const r = await api.put('/system/company', company);
            setCompany(r.data.data);
            notify.success('Company settings saved');
        } catch (err) { notify.apiError(err); }
        finally { setSavingCompany(false); }
    };

    const setC = (k, v) => setCompany((c) => ({ ...c, [k]: v }));

    if (loading) return <div className="text-center py-5"><div className="spinner-border text-success" /></div>;

    const ENTITY_LABELS = { projects: 'Projects', units: 'Units', bookings: 'Bookings', payments: 'Payments', expenses: 'Expenses', suppliers: 'Suppliers', materials: 'Materials', leads: 'Leads', bank_accounts: 'Bank Accounts', fixed_assets: 'Fixed Assets' };

    return (
        <div>
            <h4 className="mb-1">{t('systemAdministration')}</h4>
            <p className="text-muted mb-3">{t('systemAdminDesc')}</p>

            <div className="btn-group mb-3 flex-wrap">
                <button className={`btn btn-sm ${tab === 'modules' ? 'btn-primary' : 'btn-outline-secondary'}`} onClick={() => setTab('modules')}>Modules</button>
                <button className={`btn btn-sm ${tab === 'users' ? 'btn-primary' : 'btn-outline-secondary'}`} onClick={() => setTab('users')}>Users</button>
                <button className={`btn btn-sm ${tab === 'company' ? 'btn-primary' : 'btn-outline-secondary'}`} onClick={() => setTab('company')}>Company</button>
                <button className={`btn btn-sm ${tab === 'appearance' ? 'btn-primary' : 'btn-outline-secondary'}`} onClick={() => setTab('appearance')}>Appearance</button>
                <button className={`btn btn-sm ${tab === 'recycle' ? 'btn-primary' : 'btn-outline-secondary'}`} onClick={() => setTab('recycle')}>Recycle Bin</button>
            </div>

            {tab === 'modules' && (
                <div className="card table-card">
                    <div className="table-responsive">
                        <table className="table table-hover mb-0">
                            <thead className="table-light"><tr><th>Module</th><th>Key</th><th>Status</th><th>Action</th></tr></thead>
                            <tbody>
                                {modules.map((m) => (
                                    <tr key={m.id}>
                                        <td>{tl(m, 'name')}</td>
                                        <td><code>{m.key}</code></td>
                                        <td>{m.is_active ? <span className="badge bg-success">Active</span> : <span className="badge bg-secondary">Inactive</span>}</td>
                                        <td>{hasRole('super_admin') ? (
                                            <button className="btn btn-sm btn-outline-primary" onClick={() => toggleModule(m.id, m.is_active)}>{m.is_active ? 'Disable' : 'Enable'}</button>
                                        ) : <span className="text-muted small">Super admin only</span>}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {tab === 'users' && (
                <div className="card table-card">
                    <div className="table-responsive">
                        <table className="table table-hover mb-0">
                            <thead className="table-light"><tr><th>Name</th><th>Email</th><th>Role</th><th>Active</th><th>Deleted</th><th>Actions</th></tr></thead>
                            <tbody>
                                {users.map((u) => (
                                    <tr key={u.id} className={u.deleted_at ? 'table-secondary' : ''}>
                                        <td>{u.name}</td><td>{u.email}</td>
                                        <td><span className="badge bg-dark text-uppercase">{u.role}</span></td>
                                        <td>{u.is_active ? <span className="text-success">Yes</span> : <span className="text-danger">No</span>}</td>
                                        <td>{u.deleted_at ? new Date(u.deleted_at).toLocaleDateString() : '—'}</td>
                                        <td>
                                            {u.role !== 'super_admin' && !u.deleted_at && (<>
                                                <button className="btn btn-sm btn-outline-warning me-1" onClick={() => setActive(u.id, !u.is_active)}>{u.is_active ? 'Deactivate' : 'Activate'}</button>
                                                <button className="btn btn-sm btn-outline-danger" onClick={() => softDelUser(u.id)}>Delete</button>
                                            </>)}
                                            {u.deleted_at && <button className="btn btn-sm btn-outline-success" onClick={() => restoreUser(u.id)}>Restore</button>}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {tab === 'company' && company && (
                <div className="card table-card">
                    <div className="card-body">
                        <div className="d-flex align-items-center gap-3 mb-3">
                            {company.logo_url ? (
                                <img src={company.logo_url} alt="logo" style={{ height: 48, maxWidth: 120, objectFit: 'contain' }} />
                            ) : (
                                <span className="user-avatar" style={{ width: 48, height: 48 }}>{(company.name || 'C')[0]}</span>
                            )}
                            <div>
                                <div className="fw-semibold">{company.name}</div>
                                <div className="text-muted small">Company code: <code>{company.code}</code> · Single company setup</div>
                            </div>
                        </div>
                        <form onSubmit={saveCompany}>
                            <div className="row g-3">
                                <div className="col-md-6">
                                    <label className="form-label small fw-semibold">Company Name *</label>
                                    <input className="form-control" required value={company.name || ''} onChange={(e) => setC('name', e.target.value)} />
                                </div>
                                <div className="col-md-6">
                                    <label className="form-label small fw-semibold">Legal Name</label>
                                    <input className="form-control" value={company.legal_name || ''} onChange={(e) => setC('legal_name', e.target.value)} />
                                </div>
                                <div className="col-md-6">
                                    <label className="form-label small fw-semibold">Address</label>
                                    <textarea className="form-control" rows={2} value={company.address || ''} onChange={(e) => setC('address', e.target.value)} />
                                </div>
                                <div className="col-md-6">
                                    <label className="form-label small fw-semibold">Phone</label>
                                    <input className="form-control" value={company.phone || ''} onChange={(e) => setC('phone', e.target.value)} />
                                </div>
                                <div className="col-md-6">
                                    <label className="form-label small fw-semibold">Email</label>
                                    <input type="email" className="form-control" value={company.email || ''} onChange={(e) => setC('email', e.target.value)} />
                                </div>
                                <div className="col-md-6">
                                    <label className="form-label small fw-semibold">Website / Link</label>
                                    <input className="form-control" placeholder="https://…" value={company.website || ''} onChange={(e) => setC('website', e.target.value)} />
                                </div>
                                <div className="col-md-6">
                                    <label className="form-label small fw-semibold">Logo URL</label>
                                    <input className="form-control" placeholder="/uploads/logo.png or https://…" value={company.logo_url || ''} onChange={(e) => setC('logo_url', e.target.value)} />
                                </div>
                                <div className="col-md-3">
                                    <label className="form-label small fw-semibold">Tax ID</label>
                                    <input className="form-control" value={company.tax_id || ''} onChange={(e) => setC('tax_id', e.target.value)} />
                                </div>
                                <div className="col-md-3">
                                    <label className="form-label small fw-semibold">Currency</label>
                                    <input className="form-control" maxLength={3} value={company.currency_code || 'BDT'} onChange={(e) => setC('currency_code', e.target.value.toUpperCase())} />
                                </div>
                            </div>
                            <button type="submit" className="btn btn-primary mt-3" disabled={savingCompany}>
                                {savingCompany ? 'Saving…' : 'Save Company Settings'}
                            </button>
                        </form>
                    </div>
                </div>
            )}

            {tab === 'appearance' && (
                <div className="row g-3">
                    <div className="col-md-6">
                        <div className="card table-card">
                            <div className="card-body">
                                <h6 className="fw-semibold">Theme</h6>
                                <p className="text-muted small">Light or dark mode.</p>
                                <button type="button" className="btn btn-outline-secondary" onClick={toggleMode}>
                                    <i className={`bi ${mode === 'light' ? 'bi-moon-stars' : 'bi-sun'} me-2`} />
                                    Switch to {mode === 'light' ? 'Dark' : 'Light'} mode
                                </button>
                                <div className="mt-2 small text-muted">Current: <strong className="text-capitalize">{mode}</strong></div>
                            </div>
                        </div>
                    </div>
                    <div className="col-md-6">
                        <div className="card table-card">
                            <div className="card-body">
                                <h6 className="fw-semibold">Language</h6>
                                <p className="text-muted small">Interface language.</p>
                                <button type="button" className="btn btn-outline-secondary" onClick={() => setLang(lang === 'en' ? 'bn' : 'en')}>
                                    <i className="bi bi-translate me-2" />
                                    {lang === 'en' ? 'Switch to বাংলা' : 'Switch to English'}
                                </button>
                                <div className="mt-2 small text-muted">Current: <strong>{lang === 'en' ? 'English' : 'বাংলা'}</strong></div>
                            </div>
                        </div>
                    </div>
                    <div className="col-12">
                        <div className="card table-card">
                            <div className="card-body">
                                <h6 className="fw-semibold">Primary Color</h6>
                                <p className="text-muted small">Choose the app accent color.</p>
                                <div className="d-flex gap-3 align-items-center flex-wrap">
                                    {(colors || Object.keys(COLOR_HEX)).map((c) => (
                                        <button key={c} type="button" className="btn p-0 rounded-circle border"
                                            style={{ width: 34, height: 34, background: COLOR_HEX[c] || c, outline: color === c ? '3px solid var(--text)' : 'none', outlineOffset: 2 }}
                                            onClick={() => setColor(c)} title={c} />
                                    ))}
                                    <span className="text-muted small ms-2 text-capitalize">Selected: <strong>{color}</strong></span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {tab === 'recycle' && (
                <div>
                    <select className="form-select form-select-sm mb-3" style={{ width: 240 }} value={entity} onChange={(e) => setEntity(e.target.value)}>
                        {(entities.length ? entities : Object.keys(ENTITY_LABELS)).map((en) => (
                            <option key={en} value={en}>{ENTITY_LABELS[en] || en}</option>
                        ))}
                    </select>
                    <div className="card table-card">
                        <div className="table-responsive">
                            <table className="table table-sm mb-0">
                                <thead className="table-light"><tr><th>ID</th><th>Info</th><th>Deleted At</th><th></th></tr></thead>
                                <tbody>
                                    {deleted.length === 0 && <tr><td colSpan={4} className="text-center text-muted py-3">No deleted records</td></tr>}
                                    {deleted.map((row) => (
                                        <tr key={row.id}>
                                            <td>{row.id}</td>
                                            <td>{row.name || row.booking_no || row.code || row.email || row.title || row.invoice_no || `Record #${row.id}`}</td>
                                            <td>{row.deleted_at ? new Date(row.deleted_at).toLocaleString() : '—'}</td>
                                            <td><button className="btn btn-sm btn-outline-success" onClick={() => restoreEntity(row.id)}>Restore</button></td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}